import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { NewsImage } from '../types/news';

export function useNewsImages() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchImagesByArticleId = async (articleId: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('news_images')
        .select('*')
        .eq('article_id', articleId)
        .order('position');

      if (fetchError) {
        console.error('Error fetching images:', fetchError);
        return [];
      }

      // Transform the URLs to public URLs
      const transformedData = data?.map(img => {
        if (!img.url) return img;
        
        if (img.url.startsWith('http')) {
          return img;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('news')
          .getPublicUrl(img.url);

        return {
          ...img,
          url: publicUrl
        };
      });

      return transformedData as NewsImage[];
    } catch (err: any) {
      console.error('Error fetching images:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const addImage = async (image: Omit<NewsImage, 'id' | 'created_at'>) => {
    try {
      setLoading(true);
      setError(null);

      if (!user) {
        throw new Error('Utilizador não autenticado');
      }

      if (!image.url) {
        throw new Error('URL da imagem é obrigatória');
      }

      const { data, error: insertError } = await supabase
        .from('news_images')
        .insert([{
          ...image,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      return data as NewsImage;
    } catch (err: any) {
      console.error('Error adding image:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateImage = async (id: string, updates: Partial<NewsImage>) => {
    try {
      setLoading(true);
      setError(null);

      if (!user) {
        throw new Error('Utilizador não autenticado');
      }

      const { data, error: updateError } = await supabase
        .from('news_images')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      return data as NewsImage;
    } catch (err: any) {
      console.error('Error updating image:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteImage = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      if (!user) {
        throw new Error('Utilizador não autenticado');
      }

      const { error: deleteError } = await supabase
        .from('news_images')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      return true;
    } catch (err: any) {
      console.error('Error deleting image:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    fetchImagesByArticleId,
    addImage,
    updateImage,
    deleteImage
  };
}